import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MixedObject } from '@core/interfaces/mixed-object';
import { Block } from '@core/models/block/block.model';
import { SubSink } from '@core/utils/subsink';
import { getImgSizeByContainerSize } from '@shared/helpers/image-url-helpers';
import { isVideo } from '@shared/helpers/media-helpers';
import { ImageSrcService } from '@shared/services/image-src.service';

@Component({
  selector: '[app-image-block]',
  templateUrl: './image-block.component.html',
  styleUrls: ['./image-block.component.scss'],
  standalone: false,
})
export class ImageBlockComponent implements AfterViewInit, OnDestroy {
  @Input() isHovered: boolean;
  @Input() item: Block;
  @Input() blockStyles: MixedObject = {};
  @ViewChild('imageWrapper', {static: true}) imgWrapperRef!: ElementRef;
  imageUrl = '';
  loaded = false;
  eager = false;
  private subSink = new SubSink();

  constructor(private imageSrcService: ImageSrcService, private changeDetectorRef: ChangeDetectorRef,) {
  }

  get aspectRatioDimensions() {
    const dim = this.item.imageDimensions || this.item.media?.dimensions;

    return dim || null;
  }

  get directStyles(): MixedObject {
    const styles = this.blockStyles;
    return this.isHovered
      ? {
        ...styles.basic,
        ...styles.hover,
      }
      : styles.basic;
  }

  @HostBinding('style.aspectRatio') get aspectRatio(): string {
    const {objectFit, width, height} = this.directStyles;
    if (width && height) {
      return '';
    }
    const dim = this.aspectRatioDimensions;
    if (!dim || objectFit === 'cover') {
      return '';
    }
    return `${dim.width} / ${dim.height}`;
  }

  @HostBinding('style.backgroundColor') get backgroundColor(): string {
    return !this.item.useGrayPhotoPlaceholder || this.loaded
      ? ''
      : 'rgba(0, 0, 0, 0.1)';
  }

  @HostBinding('style.width') get width(): string {
    if (this.loaded) {
      return '';
    }

    const {width} = this.directStyles;

    if (width) {
      return '';
    }

    const dimensions = this.aspectRatioDimensions;
    if (!dimensions) {
      return '';
    }

    return `${dimensions.width}px`;
  }

  get image(): string {
    const m = this.item.media ?? this.item.image;
    return m?.urls.full || m?.fullUrl || '';
  }

  onLoadImage() {
    this.loaded = true;
  }

  getImageUrl(size?: number): string {
    // Pobieramy szerokość z argumentu lub innerWidth
    const screenWidth = size ? size : innerWidth;
    const width = getImgSizeByContainerSize(screenWidth);
    try {
      const img = this.item.media || this.item.image;
      if (img?.urls) {
        if (isVideo(img)) {
          return img.urls.thumbnails?.frame1;
        }
        return img.urls.thumbnails?.['mini' + width] || img.urls.full;
      }
    } catch (e) {
      console.log(e);
    }
    const url = this.image;
    if (url.startsWith('data:image')) {
      return url;
    }
    const glue = url.includes('?') ? '&' : '?';

    return `${url}${glue}size=${width}`;
  }

  ngAfterViewInit(): void {
    this.imageSrcService.observeVisibilityChanges(
      this.imgWrapperRef.nativeElement,
      (rect) => {
        this.imageUrl = this.getImageUrl(rect.width);
        // Obrazy w pierwszym ekranie (i tuż pod nim) ładujemy od razu - natywny lazy w Safari/iOS
        // opóźnia je o kilka sekund, a src i tak ustawiamy dopiero gdy kontener jest widoczny.
        this.eager = rect.top < innerHeight * 1.5;
        if (!this.eager) {
          // Reszta: przełączamy na eager z dużym wyprzedzeniem przed scrollem, żeby kafel nie
          // wjeżdżał pusty (lazy startuje dopiero tuż przed ekranem).
          this.imageSrcService.observeNearViewport(this.imgWrapperRef.nativeElement, () => {
            this.eager = true;
            this.changeDetectorRef.markForCheck();
          });
        }
        this.changeDetectorRef.markForCheck();
      },
    );
  }

  ngOnDestroy(): void {
    this.imageSrcService.disconnectObserver(this.imgWrapperRef.nativeElement);
    this.imageSrcService.unobserveNearViewport(this.imgWrapperRef.nativeElement);
    this.subSink.unsubscribe();
  }

  get imageSrc() {
    try {
      return this.imageUrl.includes('mp4')
        ? this.item.thumbnailsUrls?.[0] || this.imageUrl
        : this.imageUrl;
    } catch (e) {
      console.error(e);
      return this.imageUrl;
    }
  }

  getImageStyle() {
    const {width, height, minHeight, objectFit} = this.directStyles;
    const size = width?.includes('%')
      ? {
        width: '100%',
        height,
        minHeight,
      }
      : {
        width,
        height,
        minHeight,
      };

    return {
      ...size,
      objectFit,
    };
  }

  getIconStyle() {
    const {width} = this.directStyles;

    const size = width?.includes('%')
      ? {
        width: '5rem',
      }
      : {
        width: '30px',
      };
    return {
      ...size,
    };
  }
}
